/**
 * React Query hooks for workspace (Vault Org Holder) team management,
 * scoped to the platform-level Workspace Controls page.
 *
 * Mirrors the arche-local copy in arche/arches/fias360/hooks/useVaultTeam.ts
 * — query keys come from the central `queryKeys.vault.*` factory so the
 * two consumers share cache, and an invitation accepted/cancelled in
 * either place refreshes the other.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

type Role = 'owner' | 'admin' | 'member' | 'viewer';

export function useWorkspaceMembers(holderId: string | null) {
  return useQuery({
    queryKey: queryKeys.vault.team.members(holderId ?? ''),
    queryFn: () => api.listVaultHolderMembers(holderId!),
    select: (data) => data.members,
    enabled: !!holderId,
  });
}

export function useWorkspaceInvitations(holderId: string | null) {
  return useQuery({
    queryKey: queryKeys.vault.team.invitations(holderId ?? ''),
    queryFn: () => api.listVaultHolderInvitations(holderId!),
    select: (data) => data.invitations,
    enabled: !!holderId,
  });
}

export function useInviteWorkspaceMember(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { inviteeEmail: string; role: Role }) =>
      api.inviteVaultMember(holderId!, input),
    onSuccess: () => {
      if (holderId) qc.invalidateQueries({ queryKey: queryKeys.vault.team.invitations(holderId) });
    },
  });
}

export function useCancelWorkspaceInvitation(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) => api.cancelVaultInvitation(invitationId),
    onSuccess: () => {
      if (holderId) qc.invalidateQueries({ queryKey: queryKeys.vault.team.invitations(holderId) });
    },
  });
}

export function useUpdateWorkspaceMemberRole(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { membershipId: string; role: Role }) =>
      api.updateVaultMembershipRole(args.membershipId, args.role),
    onSuccess: () => {
      if (holderId) qc.invalidateQueries({ queryKey: queryKeys.vault.team.members(holderId) });
    },
  });
}

export function useRevokeWorkspaceMember(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (membershipId: string) => api.revokeVaultMembership(membershipId),
    onSuccess: () => {
      if (holderId) qc.invalidateQueries({ queryKey: queryKeys.vault.team.members(holderId) });
    },
  });
}

export function useDeleteWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (holderId: string) => api.deleteVaultOrgHolder(holderId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.vault.me.memberships() });
    },
  });
}

export function useCreateWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (displayName: string) => api.createVaultOrgHolder(displayName),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.vault.me.memberships() });
    },
  });
}
